import { type NodeOption } from "@skyltmax/ds9"
import { mergeAttributes, type Extensions } from "@tiptap/core"
import Highlight from "@tiptap/extension-highlight"
import Image from "@tiptap/extension-image"
import Link from "@tiptap/extension-link"
import Paragraph from "@tiptap/extension-paragraph"
import Placeholder from "@tiptap/extension-placeholder"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import Typography from "@tiptap/extension-typography"
import Underline from "@tiptap/extension-underline"
import StarterKit from "@tiptap/starter-kit"

interface StyledParagraphOptions<T> {
  styles: T[]
  HTMLAttributes: Record<string, any>
}

export const createCustomParagraph = <T extends string>(styles: T[]) => {
  return Paragraph.extend<StyledParagraphOptions<T>>({
    addOptions() {
      return {
        styles,
        HTMLAttributes: {},
      }
    },

    addAttributes() {
      return {
        style: {
          default: styles[0],
          rendered: false,
          parseHTML: element => element.getAttribute("class"),
        },
      }
    },

    renderHTML({ node, HTMLAttributes }) {
      const hasStyle = this.options.styles.includes(node.attrs.style)
      const style = hasStyle ? node.attrs.style : this.options.styles[0]

      return [`p`, mergeAttributes({ class: style }, this.options.HTMLAttributes, HTMLAttributes), 0]
    },
  })
}

type ParagraphStyle = "paragraph" | "paragraph-sm" | "paragraph-xs" | "paragraph-2xs"

const paragraphStyles: ParagraphStyle[] = ["paragraph", "paragraph-sm", "paragraph-xs", "paragraph-2xs"]

export const siteNodes: NodeOption<ParagraphStyle>[] = [
  { node: "paragraph", param: "paragraph", label: "Paragraph" },
  { node: "paragraph", param: "paragraph-sm", label: "Paragraph small" },
  { node: "paragraph", param: "paragraph-xs", label: "Paragraph xs" },
  { node: "paragraph", param: "paragraph-2xs", label: "Paragraph 2xs" },
  { node: "heading", param: 1, label: "Heading 1" },
  { node: "heading", param: 2, label: "Heading 2" },
  { node: "heading", param: 3, label: "Heading 3" },
  { node: "heading", param: 4, label: "Heading 4" },
  { node: "heading", param: 5, label: "Heading 5" },
  { node: "heading", param: 6, label: "Heading 6" },
]

// Default extension set for writing content for the client site.
export const siteStack: Extensions = [
  StarterKit.configure({ paragraph: false }),
  createCustomParagraph(paragraphStyles),
  Placeholder,
  Underline,
  Link.configure({ openOnClick: false, HTMLAttributes: { target: "_self", rel: "noopener" } }),
  Subscript,
  Superscript,
  Highlight,
  Image.configure({ inline: true }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Typography.configure({
    openDoubleQuote: false,
    closeDoubleQuote: false,
    openSingleQuote: false,
    closeSingleQuote: false,
  }),
]

export default siteStack
